import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ConfirmModal, ModalShell } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type EmailTemplateRow = {
  key: string;
  name: string;
  description: string;
  tokens: string[];
  subject: string;
  bodyHtml: string;
  isActive: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  source: 'db' | 'default';
};

/** Bloque editable = un elemento visible del mail (párrafo, título, celda, etc.). */
type EmailBlock = {
  id: string;
  path: number[];
  html: string;
  text: string;
  role: string;
};

type EditorSelection = { kind: 'subject' } | { kind: 'block'; id: string } | null;

const BLOCK_ATTR = 'data-cf-block';
/** Zona que el sistema completa al enviar (token suelto): visible pero no editable. */
const AUTO_ATTR = 'data-cf-auto';

/** Elementos que pueden representar un bloque editable del mail. */
const BLOCK_TAGS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'td',
  'th',
  'li',
  'div',
  'a',
  'span',
  'strong',
  'em',
  'b',
  'i',
  'small',
  'blockquote',
  'caption',
]);

/** Tags inline que se conservan al editar el texto de un bloque. */
const INLINE_ALLOWED_TAGS = new Set([
  'STRONG',
  'B',
  'EM',
  'I',
  'U',
  'BR',
  'SPAN',
  'A',
  'SMALL',
  'SUP',
  'SUB',
]);

/**
 * Un bloque editable nunca puede contener estructura (tablas del layout, otros
 * párrafos): al editarlo se conserva solo contenido inline, así que un elemento
 * con estructura adentro queda fuera y editamos sus descendientes.
 */
const STRUCTURAL_SELECTOR = 'table,tr,td,th,p,div,ul,ol,li,h1,h2,h3,h4,h5,h6,blockquote';

/**
 * Texto que son puros tokens: no hay nada que editar ahí, el contenido lo pone
 * el sistema al enviar. Contempla varios seguidos ({{descripcion_html}} y
 * {{vence_html}} van juntos en el mismo nodo de texto).
 */
const TOKEN_ONLY_RE = /^(\s*\{\{[\w_]+\}\}\s*)+$/;
const DIACRITICS_RE = new RegExp('[\\u0300-\\u036f]', 'g');

function normalizeBlockText(value: string | null | undefined): string {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function isDecorativeText(text: string): boolean {
  return ['COOFIT', 'C'].includes(text) || /^[^\p{L}\p{N}]+$/u.test(text);
}

function toPreviewSrcDoc(html: string): string {
  const trimmed = html.trim();
  if (!trimmed) return '';
  if (/^<!doctype/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) return trimmed;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>html,body{margin:0;padding:12px;background:#fff;}</style></head><body>${trimmed}</body></html>`;
}

function serializeEmailDocument(doc: Document): string {
  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
}

function parseEmailDocument(html: string): Document | null {
  if (typeof window === 'undefined') return null;
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return doc?.body ? doc : null;
}

/** Un bloque escondido (preheader, spacers) no se ofrece para editar. */
function isHiddenForEmailEditor(element: Element): boolean {
  let current: Element | null = element;
  while (current) {
    const style = (current.getAttribute('style') || '').toLowerCase().replace(/\s+/g, '');
    if (
      style.includes('display:none') ||
      style.includes('mso-hide:all') ||
      style.includes('max-height:0') ||
      style.includes('max-width:0') ||
      style.includes('opacity:0')
    ) {
      return true;
    }
    current = current.parentElement;
  }
  return false;
}

/** Texto propio del elemento (ignora tokens sueltos y caracteres decorativos). */
function hasOwnEditableText(element: Element): boolean {
  return Array.from(element.childNodes).some((node) => {
    if (node.nodeType !== Node.TEXT_NODE) return false;
    const text = normalizeBlockText(node.nodeValue);
    if (!text || TOKEN_ONLY_RE.test(text) || isDecorativeText(text)) return false;
    return true;
  });
}

function elementPathFromBody(element: Element, body: HTMLElement): number[] {
  const path: number[] = [];
  let current: Node | null = element;
  while (current && current !== body) {
    const parentNode: Node | null = current.parentNode;
    if (!parentNode) break;
    path.unshift(Array.prototype.indexOf.call(parentNode.childNodes, current));
    current = parentNode;
  }
  return path;
}

function resolveNodePath(body: HTMLElement, path: number[]): Node | null {
  let current: Node | null = body;
  for (const index of path) {
    current = current?.childNodes[index] ?? null;
    if (!current) return null;
  }
  return current;
}

/**
 * Bloques editables = elementos más externos que tienen texto propio visible.
 * Trabajar a nivel de elemento (y no de nodo de texto) evita partir una frase
 * en varios campos cuando lleva un <strong> o un token en el medio.
 */
function collectBlockElements(body: HTMLElement): Element[] {
  const candidates = Array.from(body.querySelectorAll('*')).filter((element) => {
    const tag = element.tagName.toLowerCase();
    if (!BLOCK_TAGS.has(tag)) return false;
    if (element.querySelector(STRUCTURAL_SELECTOR)) return false;
    if (!hasOwnEditableText(element)) return false;
    return !isHiddenForEmailEditor(element);
  });
  return candidates.filter(
    (element) => !candidates.some((other) => other !== element && other.contains(element)),
  );
}

/** Texto legible del bloque: los <br> cuentan como separador. */
function readableBlockText(element: Element): string {
  const parts: string[] = [];
  const walk = (node: Node): void => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) parts.push(String(child.nodeValue || ''));
      else if ((child as Element).tagName?.toLowerCase() === 'br') parts.push(' ');
      else walk(child);
    }
  };
  walk(element);
  return normalizeBlockText(parts.join(''));
}

/** Los títulos de las tarjetas del layout son versalitas en negrita. */
function isCardTitle(element: Element): boolean {
  const style = (element.getAttribute('style') || '').toLowerCase().replace(/\s+/g, '');
  return (
    style.includes('text-transform:uppercase') ||
    style.includes('font-weight:800') ||
    style.includes('font-weight:700')
  );
}

/**
 * Nombre de la sección: primero por estructura del layout (clases estables del
 * chrome Coofit) y recién después por palabras clave del texto.
 */
function getBlockRole(element: Element, text: string): string {
  const tag = element.tagName.toLowerCase();
  if (element.closest('.bottom-bar')) return 'footer';
  if (element.classList.contains('header-title')) return 'headline';
  if (tag === 'a' || element.closest('.cta-btn')) return 'linkText';
  if (element.closest('.detail-card')) return isCardTitle(element) ? 'appointmentDetails' : 'detailRow';
  if (element.closest('.warn-card')) return isCardTitle(element) ? 'importantNotice' : 'importantNoticeText';
  if (element.closest('.info-card')) return isCardTitle(element) ? 'infoCardTitle' : 'infoCardText';
  if (element.closest('.contact-card')) return 'contactHelp';
  if (/^h[1-3]$/.test(tag)) return 'headline';

  const normalized = text
    .normalize('NFD')
    .replace(DIACRITICS_RE, '')
    .toLowerCase();

  if (normalized.startsWith('hola')) return 'greeting';
  if (normalized.includes('recordamos') || normalized.includes('reservado')) return 'mainMessage';
  if (
    normalized.includes('datos de tu cita') ||
    normalized.includes('detalle') ||
    normalized.includes('nuevo horario')
  ) {
    return 'appointmentDetails';
  }
  if (['fecha', 'hora', 'profesional', 'direccion', 'telefono', 'fecha y hora'].includes(normalized)) {
    return 'detailLabel';
  }
  if (normalized.startsWith('recordatorio') || normalized.includes('confirmacion')) return 'headline';
  if (normalized.startsWith('clinica')) return 'clinicName';
  if (normalized.includes('importante') || normalized.includes('no respondas')) return 'importantNotice';
  if (normalized.includes('necesitas comunicarte') || normalized.includes('contacto')) return 'contactHelp';
  if (normalized.includes('equipo de')) return 'signature';
  if (normalized.includes('enviado mediante') || normalized.includes('protegidos')) return 'footer';
  return 'editableSection';
}

function extractEmailBlocks(html: string): EmailBlock[] {
  const doc = parseEmailDocument(html);
  if (!doc) return [];
  const body = doc.body;
  return collectBlockElements(body).map((element) => {
    const path = elementPathFromBody(element, body);
    const text = readableBlockText(element);
    return {
      id: path.join('.'),
      path,
      html: element.innerHTML,
      text,
      role: getBlockRole(element, text),
    };
  });
}

/**
 * Marca los bloques con data-cf-block antes de pedir la preview, para poder
 * mapear un click dentro del iframe al campo correspondiente. Solo se usa para
 * la vista previa: lo que se guarda es siempre el HTML limpio.
 */
function annotateEmailBlocks(html: string): string {
  const doc = parseEmailDocument(html);
  if (!doc) return html;
  const body = doc.body;
  const blockElements = collectBlockElements(body);
  for (const element of blockElements) {
    element.setAttribute(BLOCK_ATTR, elementPathFromBody(element, body).join('.'));
  }

  const isInsideBlock = (node: Node): boolean =>
    blockElements.some((block) => block === node || block.contains(node));

  // 1) Contenedores cuyo contenido completo es un token ({{titulo}} del header, etc.).
  const autoElements = Array.from(body.querySelectorAll('*')).filter((element) => {
    if (isInsideBlock(element) || blockElements.some((block) => element.contains(block))) return false;
    if (!TOKEN_ONLY_RE.test(normalizeBlockText(element.textContent))) return false;
    return !isHiddenForEmailEditor(element);
  });
  for (const element of autoElements) {
    if (autoElements.some((other) => other !== element && other.contains(element))) continue;
    element.setAttribute(AUTO_ATTR, '');
  }

  // 2) Tokens sueltos entre medio del cuerpo ({{descripcion_html}}, {{acciones_conexion}}):
  // no tienen contenedor propio, así que les damos uno solo para la vista previa.
  const walker = doc.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  const loose: Text[] = [];
  let node = walker.nextNode();
  while (node) {
    const parent = node.parentElement;
    if (
      parent &&
      !isInsideBlock(node) &&
      !parent.closest(`[${AUTO_ATTR}]`) &&
      TOKEN_ONLY_RE.test(normalizeBlockText(node.nodeValue)) &&
      !isHiddenForEmailEditor(parent)
    ) {
      loose.push(node as Text);
    }
    node = walker.nextNode();
  }
  for (const textNode of loose) {
    const wrapper = doc.createElement('div');
    wrapper.setAttribute(AUTO_ATTR, '');
    textNode.parentNode?.replaceChild(wrapper, textNode);
    wrapper.appendChild(textNode);
  }

  return serializeEmailDocument(doc);
}

/** Deja solo tags inline seguros: contentEditable suele inyectar divs y estilos. */
function sanitizeInlineHtml(value: string): string {
  if (typeof window === 'undefined') return value;
  const doc = new DOMParser().parseFromString(`<div id="cf-root">${value}</div>`, 'text/html');
  const root = doc.getElementById('cf-root');
  if (!root) return value;

  const clean = (parent: Element): void => {
    for (const child of Array.from(parent.children)) {
      clean(child);
      const tag = child.tagName.toUpperCase();
      if (!INLINE_ALLOWED_TAGS.has(tag)) {
        const fragment = doc.createDocumentFragment();
        if (child.previousSibling) fragment.appendChild(doc.createElement('br'));
        while (child.firstChild) fragment.appendChild(child.firstChild);
        child.replaceWith(fragment);
        continue;
      }
      const allowed = tag === 'A' ? ['href', 'target', 'rel', 'style'] : ['style'];
      for (const attribute of Array.from(child.attributes)) {
        if (!allowed.includes(attribute.name.toLowerCase())) child.removeAttribute(attribute.name);
      }
      if (/^\s*javascript:/i.test(child.getAttribute('href') || '')) child.removeAttribute('href');
    }
  };

  clean(root);
  return root.innerHTML;
}

function updateEmailBlockHtml(html: string, path: number[], innerHtml: string): string {
  const doc = parseEmailDocument(html);
  if (!doc) return html;
  const node = resolveNodePath(doc.body, path);
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return html;
  (node as Element).innerHTML = innerHtml;
  return serializeEmailDocument(doc);
}

const PREVIEW_STYLE = `
[${BLOCK_ATTR}]{cursor:pointer;border-radius:6px;transition:background-color .12s ease,box-shadow .12s ease;}
[${BLOCK_ATTR}]:hover{background-color:rgba(20,184,166,.12)!important;box-shadow:0 0 0 2px rgba(20,184,166,.5);}
[${BLOCK_ATTR}].cf-active{background-color:rgba(20,184,166,.18)!important;box-shadow:0 0 0 2px #14b8a6;}
[${AUTO_ATTR}]{cursor:not-allowed;border-radius:6px;transition:background-color .12s ease,box-shadow .12s ease;}
[${AUTO_ATTR}]:hover{background-color:rgba(100,116,139,.10)!important;box-shadow:0 0 0 2px rgba(100,116,139,.4);}
`;

const PREVIEW_SCRIPT = `
(function(){
  var activeId=null;
  var BADGE_BASE='position:fixed;z-index:2147483647;align-items:center;gap:5px;pointer-events:none;color:#FFFFFF;font-family:system-ui,-apple-system,Segoe UI,sans-serif;font-size:11px;font-weight:600;line-height:1;padding:5px 9px;border-radius:999px;box-shadow:0 4px 12px rgba(15,23,42,.28);';
  var PENCIL='<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>';
  var LOCK='<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>';
  var badge=document.createElement('div');
  badge.setAttribute('style',BADGE_BASE+'display:none;background:#0D7D82;');
  var icon=document.createElement('span');
  icon.setAttribute('style','display:inline-flex;');
  var label=document.createElement('span');
  badge.appendChild(icon);
  badge.appendChild(label);
  document.body.appendChild(badge);
  function hideBadge(){ badge.style.display='none'; }
  function showBadge(element,editable){
    var box=element.getBoundingClientRect();
    if(!box.width&&!box.height) return hideBadge();
    icon.innerHTML=editable?PENCIL:LOCK;
    label.textContent=editable?EDIT_LABEL:AUTO_LABEL;
    badge.setAttribute('style',BADGE_BASE+'display:inline-flex;background:'+(editable?'#0D7D82':'#475569')+';');
    var top=box.top-26;
    badge.style.top=(top<4?Math.min(box.top+4,window.innerHeight-28):top)+'px';
    badge.style.left=Math.max(4,Math.min(box.left,window.innerWidth-140))+'px';
  }
  function blockFrom(node){ return node&&node.closest?node.closest('[${BLOCK_ATTR}]'):null; }
  function autoFrom(node){ return node&&node.closest?node.closest('[${AUTO_ATTR}]'):null; }
  function paint(){
    var nodes=document.querySelectorAll('[${BLOCK_ATTR}]');
    for(var i=0;i<nodes.length;i++){
      var isActive=nodes[i].getAttribute('${BLOCK_ATTR}')===activeId;
      if(isActive) nodes[i].classList.add('cf-active'); else nodes[i].classList.remove('cf-active');
    }
  }
  document.addEventListener('mouseover',function(event){
    var element=blockFrom(event.target);
    if(element) return showBadge(element,true);
    var autoElement=autoFrom(event.target);
    if(autoElement) return showBadge(autoElement,false);
    hideBadge();
  });
  document.addEventListener('mouseleave',hideBadge);
  window.addEventListener('scroll',hideBadge,true);
  document.addEventListener('click',function(event){
    var element=blockFrom(event.target);
    event.preventDefault();
    // Sobre una zona automática no cambiamos la selección: no hay nada que editar ahí.
    if(!element&&autoFrom(event.target)) return;
    activeId=element?element.getAttribute('${BLOCK_ATTR}'):null;
    paint();
    parent.postMessage({source:'coofit-email-preview',type:'select',id:activeId},'*');
  },true);
  window.addEventListener('message',function(event){
    var data=event.data||{};
    if(data.source!=='coofit-email-editor'||data.type!=='highlight') return;
    activeId=data.id||null;
    paint();
    if(!activeId) return;
    var element=document.querySelector('[${BLOCK_ATTR}="'+activeId+'"]');
    if(!element) return;
    var box=element.getBoundingClientRect();
    if(box.top<0||box.bottom>window.innerHeight) element.scrollIntoView({block:'center'});
  });
  parent.postMessage({source:'coofit-email-preview',type:'ready'},'*');
})();
`;

function buildInteractiveSrcDoc(html: string, labels: { edit: string; auto: string }): string {
  const base = toPreviewSrcDoc(html);
  if (!base) return '';
  const script = `var EDIT_LABEL=${JSON.stringify(labels.edit)};var AUTO_LABEL=${JSON.stringify(
    labels.auto,
  )};\n${PREVIEW_SCRIPT}`;
  const injection = `<style>${PREVIEW_STYLE}</style><script>${script}</script>`;
  return /<\/body>/i.test(base)
    ? base.replace(/<\/body>/i, `${injection}</body>`)
    : `${base}${injection}`;
}

const PencilIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const EmailPreviewFrame: React.FC<{
  html: string;
  title: string;
  editLabel: string;
  autoLabel: string;
  activeBlockId: string | null;
  onSelectBlock: (id: string | null) => void;
}> = ({ html, title, editLabel, autoLabel, activeBlockId, onSelectBlock }) => {
  const frameRef = useRef<HTMLIFrameElement | null>(null);
  const srcDoc = useMemo(
    () => buildInteractiveSrcDoc(html, { edit: editLabel, auto: autoLabel }),
    [autoLabel, editLabel, html],
  );

  const highlight = useCallback((id: string | null) => {
    frameRef.current?.contentWindow?.postMessage(
      { source: 'coofit-email-editor', type: 'highlight', id },
      '*',
    );
  }, []);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      const data = event.data as { source?: string; type?: string; id?: string | null } | null;
      if (data?.source !== 'coofit-email-preview') return;
      if (data.type === 'ready') highlight(activeBlockId);
      if (data.type === 'select') onSelectBlock(data.id ?? null);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [activeBlockId, highlight, onSelectBlock]);

  useEffect(() => {
    highlight(activeBlockId);
  }, [activeBlockId, highlight]);

  return (
    <iframe
      ref={frameRef}
      title={title}
      srcDoc={srcDoc}
      sandbox="allow-scripts"
      onLoad={() => highlight(activeBlockId)}
      className="h-full min-h-[280px] w-full flex-1 border-0 bg-white"
    />
  );
};

/** Campo de texto enriquecido: conserva negritas, enlaces y tokens del bloque. */
const RichTextField: React.FC<{
  value: string;
  disabled?: boolean;
  onChange: (html: string) => void;
  fieldRef: React.MutableRefObject<HTMLDivElement | null>;
}> = ({ value, disabled, onChange, fieldRef }) => {
  useEffect(() => {
    const element = fieldRef.current;
    if (!element) return;
    if (document.activeElement === element) return;
    if (element.innerHTML !== value) element.innerHTML = value;
  }, [fieldRef, value]);

  return (
    <div
      ref={fieldRef}
      role="textbox"
      aria-multiline="true"
      tabIndex={0}
      contentEditable={!disabled}
      suppressContentEditableWarning
      onInput={(event) => onChange(sanitizeInlineHtml(event.currentTarget.innerHTML))}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          document.execCommand('insertLineBreak');
        }
      }}
      onPaste={(event) => {
        event.preventDefault();
        document.execCommand('insertText', false, event.clipboardData.getData('text/plain'));
      }}
      className="email-rich-field min-h-[110px] w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm leading-relaxed text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40 dark:border-gray-600 dark:bg-gray-900 dark:text-slate-100"
    />
  );
};

export const EmailTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAdminister } = useAuth();
  const { success, error: showError } = useToast();

  const [items, setItems] = useState<EmailTemplateRow[]>([]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSignature, setPreviewSignature] = useState<string | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [areSectionsOpen, setAreSectionsOpen] = useState(false);
  // Qué se está editando: el asunto, un bloque del mail, o nada.
  // Arranca en null a propósito: el estado vacío explica cómo se usa la pantalla.
  const [selection, setSelection] = useState<EditorSelection>(null);
  /** Acción destructiva esperando confirmación en el modal. */
  const [pendingAction, setPendingAction] = useState<'restore' | 'discard' | null>(null);

  const subjectRef = useRef<HTMLInputElement | null>(null);
  const blockFieldRef = useRef<HTMLDivElement | null>(null);
  const loadedKeyRef = useRef<string | null>(null);

  const selected = useMemo(
    () => items.find((i) => i.key === selectedKey) || null,
    [items, selectedKey],
  );

  const blocks = useMemo(() => extractEmailBlocks(bodyHtml), [bodyHtml]);
  const activeBlockId = selection?.kind === 'block' ? selection.id : null;
  const activeBlock = useMemo(
    () => blocks.find((block) => block.id === activeBlockId) || null,
    [activeBlockId, blocks],
  );

  const signature = `${subject}\u0000${bodyHtml}`;
  const isPreviewStale = Boolean(previewHtml) && previewSignature !== null && previewSignature !== signature;
  const isDirty = Boolean(
    selected &&
      (subject !== selected.subject || bodyHtml !== selected.bodyHtml || isActive !== selected.isActive),
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<{ items: EmailTemplateRow[] }>('/email-templates');
      const list = Array.isArray(res.data?.items) ? res.data.items : [];
      setItems(list);
      setSelectedKey((prev) => {
        if (prev && list.some((i) => i.key === prev)) return prev;
        return list[0]?.key ?? null;
      });
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const runPreview = useCallback(
    async (
      input: { key: string; subject: string; bodyHtml: string },
      options?: { openModal?: boolean; silent?: boolean },
    ) => {
      setIsPreviewing(true);
      try {
        const res = await httpClient.post<{ subject: string; bodyHtml: string }>(
          `/email-templates/${encodeURIComponent(input.key)}/preview`,
          { subject: input.subject, bodyHtml: annotateEmailBlocks(input.bodyHtml) },
        );
        setPreviewSubject(res.data?.subject || '');
        setPreviewHtml(res.data?.bodyHtml || '');
        setPreviewSignature(`${input.subject}\u0000${input.bodyHtml}`);
        if (options?.openModal) setIsPreviewModalOpen(true);
      } catch (err) {
        if (!options?.silent) showError(err instanceof Error ? err.message : t('common.error'));
      } finally {
        setIsPreviewing(false);
      }
    },
    [showError, t],
  );

  // Al cambiar de plantilla: cargar campos y generar la previa inicial.
  // El resto de los refrescos son manuales (botón "Actualizar vista previa").
  useEffect(() => {
    if (!selected || loadedKeyRef.current === selected.key) return;
    loadedKeyRef.current = selected.key;
    setSubject(selected.subject);
    setBodyHtml(selected.bodyHtml);
    setIsActive(selected.isActive);
    setPreviewSubject('');
    setPreviewHtml('');
    setPreviewSignature(null);
    setIsAdvancedOpen(false);
    setAreSectionsOpen(false);
    setSelection(null);
    void runPreview(
      { key: selected.key, subject: selected.subject, bodyHtml: selected.bodyHtml },
      { silent: true },
    );
  }, [runPreview, selected]);

  const handleSave = async () => {
    if (!canAdminister || !selectedKey) return;
    setIsSaving(true);
    try {
      const res = await httpClient.put<{ item: EmailTemplateRow }>(
        `/email-templates/${encodeURIComponent(selectedKey)}`,
        { subject, bodyHtml, isActive },
      );
      const item = res.data?.item;
      if (item) {
        setItems((prev) => prev.map((row) => (row.key === item.key ? item : row)));
      }
      success(t('pages.emailTemplates.saved'));
      void runPreview({ key: selectedKey, subject, bodyHtml }, { silent: true });
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async () => {
    if (!canAdminister || !selectedKey) return;
    setIsSaving(true);
    try {
      const res = await httpClient.post<{ item: EmailTemplateRow }>(
        `/email-templates/${encodeURIComponent(selectedKey)}/restore-default`,
        {},
      );
      const item = res.data?.item;
      if (item) {
        setItems((prev) => prev.map((row) => (row.key === item.key ? item : row)));
        setSubject(item.subject);
        setBodyHtml(item.bodyHtml);
        setIsActive(item.isActive);
        setSelection(null);
        void runPreview(
          { key: item.key, subject: item.subject, bodyHtml: item.bodyHtml },
          { silent: true },
        );
      }
      success(t('pages.emailTemplates.restored'));
      setPendingAction(null);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!selected || !isDirty) return;
    setPendingAction(null);
    setSubject(selected.subject);
    setBodyHtml(selected.bodyHtml);
    setIsActive(selected.isActive);
    setSelection(null);
    void runPreview(
      { key: selected.key, subject: selected.subject, bodyHtml: selected.bodyHtml },
      { silent: true },
    );
  };

  const handleBlockChange = (html: string) => {
    if (!activeBlock) return;
    setBodyHtml((prev) => updateEmailBlockHtml(prev, activeBlock.path, html));
  };

  const insertToken = (token: string) => {
    const placeholder = `{{${token}}}`;

    if (activeBlock) {
      const field = blockFieldRef.current;
      if (field) {
        field.focus();
        if (document.execCommand('insertText', false, placeholder)) {
          handleBlockChange(sanitizeInlineHtml(field.innerHTML));
          return;
        }
      }
      handleBlockChange(`${activeBlock.html} ${placeholder}`);
      return;
    }

    const input = subjectRef.current;
    const start = input?.selectionStart ?? subject.length;
    const end = input?.selectionEnd ?? start;
    const next = `${subject.slice(0, start)}${placeholder}${subject.slice(end)}`;
    setSubject(next);
    window.requestAnimationFrame(() => {
      if (!input) return;
      const caret = start + placeholder.length;
      input.focus();
      input.setSelectionRange(caret, caret);
    });
  };

  const selectBlock = useCallback((id: string | null) => {
    setSelection(id ? { kind: 'block', id } : null);
  }, []);

  const refreshPreview = (openModal = false) => {
    if (!selectedKey) return;
    void runPreview({ key: selectedKey, subject, bodyHtml }, { openModal });
  };

  const blockLabel = (block: EmailBlock) => t(`pages.emailTemplates.sectionLabels.${block.role}`);

  const previewPane = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="flex-shrink-0 border-b border-slate-200 px-3 py-2.5 dark:border-gray-700">
        <button
          type="button"
          onClick={() => {
            setSelection({ kind: 'subject' });
            window.requestAnimationFrame(() => subjectRef.current?.focus());
          }}
          className={`group flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1 text-left transition ${
            selection?.kind === 'subject'
              ? 'bg-teal-50 ring-1 ring-teal-400 dark:bg-teal-950/30'
              : 'hover:bg-slate-100 dark:hover:bg-gray-700/60'
          }`}
          title={t('pages.emailTemplates.editSubject')}
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] font-medium uppercase tracking-wide text-slate-400 dark:text-slate-500">
              {t('pages.emailTemplates.subject')}
            </span>
            <span className="block truncate text-sm text-slate-800 dark:text-slate-100">
              {previewSubject || subject || '—'}
            </span>
          </span>
          <PencilIcon
            className={`h-3.5 w-3.5 flex-shrink-0 transition ${
              selection?.kind === 'subject'
                ? 'text-teal-600 dark:text-teal-300'
                : 'text-slate-300 group-hover:text-teal-600 dark:text-slate-600 dark:group-hover:text-teal-300'
            }`}
          />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden bg-slate-50 dark:bg-gray-900">
        {isPreviewing && !previewHtml ? (
          <p className="p-4 text-sm text-slate-500 dark:text-slate-400">
            {t('pages.emailTemplates.previewing')}
          </p>
        ) : previewHtml ? (
          <EmailPreviewFrame
            html={previewHtml}
            title={t('pages.emailTemplates.previewTitle')}
            editLabel={t('pages.emailTemplates.editThis')}
            autoLabel={t('pages.emailTemplates.autoFilled')}
            activeBlockId={activeBlockId}
            onSelectBlock={selectBlock}
          />
        ) : (
          <p className="p-4 text-sm text-slate-500 dark:text-slate-400">
            {t('pages.emailTemplates.previewEmpty')}
          </p>
        )}
      </div>
      <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-gray-700">
        {isPreviewStale ? (
          <span className="mr-auto inline-flex items-center gap-1.5 text-xs font-medium text-amber-600 dark:text-amber-400">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
            {t('pages.emailTemplates.previewOutdated')}
          </span>
        ) : null}
        <Button
          variant={isPreviewStale ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => refreshPreview(false)}
          isLoading={isPreviewing}
        >
          {t('pages.emailTemplates.preview')}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            if (previewHtml && !isPreviewStale) {
              setIsPreviewModalOpen(true);
              return;
            }
            refreshPreview(true);
          }}
          disabled={isPreviewing}
        >
          {t('pages.emailTemplates.openPreviewModal')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="panel-page h-full min-h-0 min-w-0 flex flex-col overflow-y-auto lg:overflow-hidden !space-y-3">
      <div className="flex-shrink-0 space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="panel-page-title">{t('pages.emailTemplates.title')}</h1>
            <p className="panel-page-subtitle">{t('pages.emailTemplates.subtitle')}</p>
          </div>
          {selected && canAdminister && isDirty ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              {t('pages.emailTemplates.unsavedChanges')}
            </span>
          ) : null}
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('pages.emailTemplates.empty')}
          </p>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1 sm:max-w-sm">
              <label
                className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200"
                htmlFor="email-template-select"
              >
                {t('pages.emailTemplates.selectTemplate')}
              </label>
              <select
                id="email-template-select"
                value={selectedKey ?? ''}
                onChange={(e) => setSelectedKey(e.target.value || null)}
                className="block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                {items.map((item) => (
                  <option
                    key={item.key}
                    value={item.key}
                    className="bg-white text-slate-900 dark:bg-gray-700 dark:text-white"
                  >
                    {item.name}
                    {!item.isActive ? ` (${t('pages.emailTemplates.inactive')})` : ''}
                  </option>
                ))}
              </select>
            </div>
            {selected ? (
              <div className="min-w-[220px] flex-1 pb-1">
                <p className="text-xs text-slate-500 dark:text-slate-400">{selected.description}</p>
                {selected.updatedAt ? (
                  <p className="mt-0.5 text-[11px] text-slate-400 dark:text-slate-500">
                    {t('pages.emailTemplates.updatedBy', {
                      user: selected.updatedBy || '—',
                      at: new Date(selected.updatedAt).toLocaleString(),
                    })}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>

      {!isLoading && selected ? (
        <div className="grid items-stretch gap-4 lg:min-h-0 lg:flex-1 lg:overflow-hidden lg:grid-cols-[minmax(0,1.35fr)_minmax(340px,1fr)]">
          <div className="flex h-[55vh] flex-col lg:h-full lg:min-h-0">{previewPane}</div>

          <aside className="flex h-full min-h-0 flex-col rounded-xl border border-slate-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                {selection ? (
                  <>
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-600 dark:text-teal-400">
                          {t('pages.emailTemplates.editing')}
                        </p>
                        <h2 className="truncate text-sm font-semibold text-slate-800 dark:text-white">
                          {selection.kind === 'subject'
                            ? t('pages.emailTemplates.subject')
                            : activeBlock
                              ? blockLabel(activeBlock)
                              : t('pages.emailTemplates.blockNotFound')}
                        </h2>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelection(null)}
                        title={t('pages.emailTemplates.closeEditor')}
                        aria-label={t('pages.emailTemplates.closeEditor')}
                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-lg leading-none text-slate-400 hover:bg-slate-200 hover:text-slate-700 dark:hover:bg-gray-700 dark:hover:text-white"
                      >
                        &times;
                      </button>
                    </div>
                    {selection.kind === 'subject' ? (
                      <>
                        <input
                          id="email-template-subject"
                          ref={subjectRef}
                          value={subject}
                          onChange={(e) => setSubject(e.target.value)}
                          disabled={!canAdminister}
                          className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/40 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:text-slate-100"
                        />
                        <p className="mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                          {t('pages.emailTemplates.subjectHint')}
                        </p>
                      </>
                    ) : activeBlock ? (
                      <RichTextField
                        key={`${selected.key}:${activeBlock.id}`}
                        value={activeBlock.html}
                        disabled={!canAdminister}
                        onChange={handleBlockChange}
                        fieldRef={blockFieldRef}
                      />
                    ) : (
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('pages.emailTemplates.blockNotFoundHint')}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="py-4 text-center">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {t('pages.emailTemplates.editorEmptyTitle')}
                    </p>
                    <p className="mx-auto mt-1 max-w-xs text-xs text-slate-500 dark:text-slate-400">
                      {t('pages.emailTemplates.editorEmptyHint')}
                    </p>
                  </div>
                )}
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                  {t('pages.emailTemplates.availableVariables')}
                </p>
                <p className="mb-2 text-[11px] text-slate-500 dark:text-slate-400">
                  {selection
                    ? t('pages.emailTemplates.insertInto', {
                        field:
                          selection.kind === 'block' && activeBlock
                            ? blockLabel(activeBlock)
                            : t('pages.emailTemplates.subject'),
                      })
                    : t('pages.emailTemplates.insertIntoNothing')}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {selected.tokens.map((token) => (
                    <button
                      key={token}
                      type="button"
                      disabled={!canAdminister || !selection}
                      onClick={() => insertToken(token)}
                      className="rounded-md border border-slate-200 bg-white px-2 py-1 font-mono text-xs text-slate-700 hover:border-teal-400 hover:bg-teal-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:border-teal-500 dark:hover:bg-teal-950/40"
                      title={t('pages.emailTemplates.insertToken')}
                    >
                      {`{{${token}}}`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setAreSectionsOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-semibold text-slate-800 dark:text-white"
                >
                  <span>
                    {t('pages.emailTemplates.sectionsTitle')}
                    <span className="ml-1.5 text-xs font-normal text-slate-400">({blocks.length})</span>
                  </span>
                  <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                    {areSectionsOpen
                      ? t('pages.emailTemplates.hideSections')
                      : t('pages.emailTemplates.showSections')}
                  </span>
                </button>
                {areSectionsOpen ? (
                  <div className="max-h-64 space-y-1 overflow-y-auto border-t border-slate-200 p-2 dark:border-gray-700">
                    {blocks.length > 0 ? (
                      blocks.map((block) => (
                        <button
                          key={block.id}
                          type="button"
                          onClick={() => selectBlock(block.id)}
                          className={`block w-full rounded-lg px-2.5 py-2 text-left transition ${
                            block.id === activeBlockId
                              ? 'bg-teal-50 ring-1 ring-teal-400 dark:bg-teal-950/30'
                              : 'hover:bg-slate-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          <span className="block text-xs font-semibold text-slate-700 dark:text-slate-100">
                            {blockLabel(block)}
                          </span>
                          <span className="block truncate text-[11px] text-slate-500 dark:text-slate-400">
                            {block.text}
                          </span>
                        </button>
                      ))
                    ) : (
                      <p className="p-2 text-xs text-slate-500 dark:text-slate-400">
                        {t('pages.emailTemplates.noEditableBlocks')}
                      </p>
                    )}
                  </div>
                ) : null}
              </div>

              <label className="flex items-start gap-2 rounded-xl border border-slate-200 p-3 text-sm text-slate-700 dark:border-gray-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={isActive}
                  disabled={!canAdminister}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                <span>{t('pages.emailTemplates.isActive')}</span>
              </label>

              <div className="rounded-xl border border-slate-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setIsAdvancedOpen((prev) => !prev)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-semibold text-slate-800 dark:text-white"
                >
                  <span>{t('pages.emailTemplates.advancedHtml')}</span>
                  <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                    {isAdvancedOpen
                      ? t('pages.emailTemplates.hideAdvanced')
                      : t('pages.emailTemplates.showAdvanced')}
                  </span>
                </button>
                {isAdvancedOpen ? (
                  <div className="border-t border-slate-200 p-3 dark:border-gray-700">
                    <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                      {t('pages.emailTemplates.advancedHtmlHint')}
                    </p>
                    <textarea
                      value={bodyHtml}
                      onChange={(e) => {
                        setBodyHtml(e.target.value);
                        setSelection(null);
                      }}
                      disabled={!canAdminister}
                      rows={14}
                      spellCheck={false}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-800 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-950 dark:text-slate-100"
                    />
                  </div>
                ) : null}
              </div>
            </div>

            {canAdminister ? (
              <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 dark:border-gray-700">
                <Button
                  variant="secondary"
                  size="sm"
                  className="mr-auto"
                  onClick={() => setPendingAction('restore')}
                  disabled={isSaving}
                >
                  {t('pages.emailTemplates.restoreDefault')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPendingAction('discard')}
                  disabled={!isDirty || isSaving}
                >
                  {t('pages.emailTemplates.discardChanges')}
                </Button>
                <Button
                  size="sm"
                  onClick={() => void handleSave()}
                  isLoading={isSaving}
                  disabled={!isDirty}
                >
                  {t('pages.emailTemplates.saveChanges')}
                </Button>
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}

      <ModalShell
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        title={t('pages.emailTemplates.previewTitle')}
        subtitle={previewSubject || undefined}
        size="full"
        bodyClassName="!p-0 !overflow-hidden flex flex-col min-h-0"
      >
        <div className="flex min-h-[70dvh] flex-1 flex-col overflow-hidden">
          {previewHtml ? (
            <EmailPreviewFrame
              html={previewHtml}
              title={t('pages.emailTemplates.previewTitle')}
              editLabel={t('pages.emailTemplates.editThis')}
              autoLabel={t('pages.emailTemplates.autoFilled')}
              activeBlockId={activeBlockId}
              onSelectBlock={selectBlock}
            />
          ) : (
            <p className="p-4 text-sm text-slate-500">{t('pages.emailTemplates.previewEmpty')}</p>
          )}
        </div>
      </ModalShell>

      <ConfirmModal
        isOpen={pendingAction != null}
        onClose={() => {
          if (!isSaving) setPendingAction(null);
        }}
        onConfirm={() => {
          if (pendingAction === 'restore') return handleRestore();
          handleDiscard();
        }}
        title={
          pendingAction === 'restore'
            ? t('pages.emailTemplates.restoreTitle')
            : t('pages.emailTemplates.discardTitle')
        }
        message={
          pendingAction === 'restore'
            ? t('pages.emailTemplates.restoreConfirm')
            : t('pages.emailTemplates.discardConfirm')
        }
        confirmText={
          pendingAction === 'restore'
            ? t('pages.emailTemplates.restoreDefault')
            : t('pages.emailTemplates.discardChanges')
        }
        cancelText={t('common.cancel')}
        variant="danger"
        isLoading={pendingAction === 'restore' && isSaving}
      >
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {pendingAction === 'restore'
            ? t('pages.emailTemplates.restoreHint', { name: selected?.name ?? '' })
            : t('pages.emailTemplates.discardHint')}
        </p>
      </ConfirmModal>
    </div>
  );
};
